import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { fadeUp, stagger } from "@/lib/anim";
import Reveal from "@/components/Reveal";
import { Plus, Minus } from "lucide-react";

const team = [
  {
    name: "Hridya P Shenoy",
    role: "Co-founder",
    meta: "12th Grade Student",
    initials: "HS",
    bio: "Co-founder of KelCalm, shaping the platform from a student's perspective — ensuring the experience feels safe, private, and genuinely built for peers.",
  },
  {
    name: "Minna Achamma Koshy",
    role: "Co-founder",
    meta: "12th Grade Student",
    initials: "MK",
    bio: "Co-founder of KelCalm, championing accessibility and stigma-free support so no student ever has to carry it alone.",
  },
  {
    name: "Dr. Anoop G",
    role: "Psychiatrist · Advisor",
    meta: "MBBS, MD (Psychiatry)",
    initials: "AG",
    advisor: true,
    bio: "Dr. Anoop G is a JIPMER-trained psychiatrist with over seven years of experience in mental healthcare, including outpatient and inpatient psychiatry, psychological interventions, psychiatric emergencies, and community mental health.",
    more: "He previously served as the Nodal Officer of the Ernakulam District Mental Health Programme and has been involved in mental health outreach and awareness initiatives among schoolchildren. He was one of 16 psychiatrists selected for the 2020 International Early Career Psychiatrist Fellowship Programme at NIMHANS and has contributed to peer-reviewed research in psychiatry. As an Advisor to KelCalm, Dr. Anoop provides clinical guidance on developing a safe, confidential, accessible, and stigma-sensitive platform for student wellbeing.",
  },
];

const Monogram = ({ initials, variant }) => {
  const bg =
    variant === "advisor"
      ? "bg-gradient-to-br from-accent to-primary text-primary-foreground"
      : variant === "b"
      ? "bg-secondary text-foreground"
      : "bg-primary text-primary-foreground";
  return (
    <div className={`relative flex h-full w-full items-center justify-center overflow-hidden ${bg}`}>
      <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-white/10" aria-hidden />
      <div className="absolute -bottom-14 -left-6 h-48 w-48 rounded-full bg-black/10" aria-hidden />
      <span className="font-serif italic text-8xl md:text-9xl font-light select-none">
        {initials}
      </span>
    </div>
  );
};

export const Team = () => {
  const [openAdvisor, setOpenAdvisor] = useState(false);

  return (
    <section
      id="team"
      className="py-24 md:py-40 px-6 md:px-12 bg-secondary/40 border-y border-border"
      data-testid="team-section"
    >
      <div className="mx-auto max-w-7xl">
        <div className="max-w-3xl mb-14 md:mb-20">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.24em] font-semibold text-primary mb-6">
              Who we are
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-serif font-light tracking-tight text-foreground text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-balance">
              The people behind KelCalm.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-xl">
              Two students who refused to accept that support should be hard to reach —
              guided by a psychiatrist who ensures every step is safe, clinical, and
              stigma-sensitive.
            </p>
          </Reveal>
        </div>

        <motion.div
          variants={stagger(0.1)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
        >
          {team.map((m, i) => {
            const variant = m.advisor ? "advisor" : i === 1 ? "b" : "a";
            const cardOpen = m.advisor && openAdvisor;
            return (
              <motion.div
                key={i}
                variants={fadeUp}
                data-testid={`team-card-${i}`}
                className={`group rounded-3xl bg-card border border-border overflow-hidden transition-all duration-500 hover:shadow-md ${
                  m.advisor ? "lg:row-span-1" : ""
                }`}
              >
                <div className="relative overflow-hidden aspect-[4/3]">
                  <Monogram initials={m.initials} variant={variant} />
                  {m.advisor && (
                    <span className="absolute top-4 left-4 rounded-full bg-background/85 backdrop-blur px-3 py-1 text-xs font-semibold text-foreground">
                      Advisor
                    </span>
                  )}
                </div>
                <div className="p-7">
                  <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary">
                    {m.role}
                  </p>
                  <h3 className="font-serif text-2xl md:text-[1.65rem] text-foreground tracking-tight mt-2 leading-tight">
                    {m.name}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1 font-medium">
                    {m.meta}
                  </p>
                  <p className="mt-4 text-muted-foreground leading-relaxed">{m.bio}</p>

                  {m.advisor && (
                    <>
                      <AnimatePresence initial={false}>
                        {cardOpen && (
                          <motion.p
                            key="more"
                            initial={{ opacity: 0, height: 0, marginTop: 0 }}
                            animate={{ opacity: 1, height: "auto", marginTop: 16 }}
                            exit={{ opacity: 0, height: 0, marginTop: 0 }}
                            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                            className="text-muted-foreground leading-relaxed overflow-hidden"
                            data-testid="advisor-more-text"
                          >
                            {m.more}
                          </motion.p>
                        )}
                      </AnimatePresence>
                      <button
                        type="button"
                        onClick={() => setOpenAdvisor((v) => !v)}
                        data-testid="advisor-toggle"
                        className="mt-5 inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground hover:bg-secondary transition-colors duration-300"
                      >
                        {cardOpen ? (
                          <>
                            <Minus className="h-4 w-4" /> Show less
                          </>
                        ) : (
                          <>
                            <Plus className="h-4 w-4" /> More about advisor
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default Team;
